import { type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';

import { type DeliveryNoteAnalyticsData } from '~/data/deliveryNote';

import { calculatePrecisionOfNumber } from '@vestigas/utils';
import { getAbbreviatedUnit } from '~/utils/unit';
import UnitUtils from '~/utils/unitUtils';
import { USER_SETTINGS_KEY } from '~/utils/usePersistedUserSettings';

import { BasicTable } from '~/components/BasicTable';

type P = {
  readonly data: DeliveryNoteAnalyticsData[];
  readonly isLoading: boolean;
  readonly selectedUnit: string;
};

type Row = {
  article: string;
  id: number;
  unit: string;
  unitKey: string;
  value: number;
};

export const DashboardDetailOverview = ({
  data,
  isLoading,
  selectedUnit,
}: P) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'value',
      sort: 'desc',
    },
  ]);

  const updateStates = useCallback(
    (data: DeliveryNoteAnalyticsData[]) => {
      if (!data) {
        return;
      }

      const newRows =
        data.map(({ article, values }, index) => ({
          article,
          id: index,
          unit: getAbbreviatedUnit(selectedUnit),
          unitKey: selectedUnit,
          value: values.reduce<number>((sum, v) => sum + (v ?? 0), 0),
        })) ?? [];

      setRows(newRows);
    },
    [selectedUnit],
  );

  const onSortModelChange = useCallback((event: GridSortModel) => {
    setSortModel(event);
  }, []);

  const getColumns = (): GridColDef[] => {
    let maxPrecision = 0;

    for (const row of rows) {
      const p = calculatePrecisionOfNumber(row.value);

      if (p > maxPrecision && p <= 2) {
        maxPrecision = p;
      }
    }

    return [
      {
        field: 'article',
        flex: 6,
        headerName: 'Artikel',
        resizable: true,
        sortable: true,
      },
      {
        field: 'value',
        flex: 2,
        headerName: 'Menge',
        renderCell: ({ value }) =>
          UnitUtils.formatDeWithPrecision_safe(
            value,
            maxPrecision,
            maxPrecision,
          ),
        resizable: true,
        sortable: true,
        type: 'number',
      },
      {
        field: 'unit',
        flex: 1,
        headerName: 'Einheit',
        resizable: true,
        sortable: true,
      },
    ];
  };

  useEffect(() => {
    updateStates(data);
  }, [data]);

  return (
    <div
      className="h-[40vh] min-h-[389px]" // 389px fits exactly 10 rows in the table
    >
      <BasicTable
        key={USER_SETTINGS_KEY.DASHBOARD_LIST}
        className="h-full rounded-md border"
        columns={getColumns()}
        isLoading={isLoading}
        rows={rows}
        sortModel={sortModel}
        userSettingsKey={USER_SETTINGS_KEY.DASHBOARD_LIST}
        hideHeader
        onSortModelChange={onSortModelChange}
      />
    </div>
  );
};

DashboardDetailOverview.displayName = 'DashboardDetailOverview';
